import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cake, Layers, LayoutGrid, UserCheck, UserPlus, Users } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ChartCard } from '@/components/shared/chart-card';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn, type SortDirection } from '@/components/shared/data-table';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FilterBar } from '@/components/shared/filter-bar';
import { MemberNameWithPositions } from '@/components/shared/member-name-with-positions';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { SimpleDonutChart } from '@/components/shared/simple-donut-chart';
import { StatCard } from '@/components/shared/stat-card';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { usePreferences } from '@/stores/preferences-store';
import { formatNumber } from '@/utils/format';

import { DepartmentLeadershipPanel } from './department-leadership-panel';
import { DepartmentUnitsPanel } from './department-units-panel';

interface MemberUnitSummary {
  id: string;
  name: string;
  roles: string[];
}

interface Person {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  contactHidden?: boolean;
  memberType: string;
  status: string;
  units?: MemberUnitSummary[];
  departmentRoles?: string[];
}

interface DepartmentDetail {
  id: string;
  name: string;
  description: string | null;
  members: Person[];
}

interface DepartmentUnitOption {
  id: string;
  name: string;
}

const UNIT_FILTER_ANY = '__any__';
const UNIT_FILTER_NONE = '__none__';

const toSortParam = (field: string, direction: SortDirection) =>
  direction === 'desc' ? `-${field}` : field;

interface DepartmentInsights {
  memberCount: number;
  activeCount: number;
  joinedThisMonth: number;
  birthdayCount: number;
  unitCount: number;
  inUnitsCount: number;
  byGender: Array<{ key: string; value: number }>;
  byType: Array<{ key: string; value: number }>;
  birthdaysThisMonth: Array<{
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  }>;
  largestUnits: Array<{ key: string; label: string; value: number }>;
}

const personLabel = (person: Pick<Person, 'firstName' | 'lastName'>): string =>
  `${person.firstName} ${person.lastName}`.trim();

const formatUnitMembership = (unit: MemberUnitSummary): string =>
  unit.roles.length > 0 ? `${unit.name} [${unit.roles.join(', ')}]` : unit.name;

const formatMemberUnits = (units: MemberUnitSummary[] | undefined): string => {
  if (!units || units.length === 0) return '—';
  return units.map(formatUnitMembership).join(', ');
};

const formatBirthdayDay = (iso: string, locale: string): string => {
  const parsed = DateTime.fromISO(iso, { zone: 'utc' }).setLocale(locale);
  if (!parsed.isValid) return '—';
  return parsed.toFormat('MMM d');
};

export const DepartmentDetailPage = () => {
  const { departmentId = '' } = useParams();
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const locale = usePreferences((state) => state.locale);
  const membership = useCurrentMembership();
  const canManageDepartments = hasPermission(membership, PERMISSIONS.PEOPLE_MANAGE_DEPARTMENTS);
  const canEditMembers = hasPermission(membership, PERMISSIONS.PEOPLE_EDIT);

  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [selectedPeople, setSelectedPeople] = useState<Person[]>([]);
  const [removeTarget, setRemoveTarget] = useState<Person | null>(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberPage, setMemberPage] = useState(1);
  const [memberPageSize, setMemberPageSize] = useState(25);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [unitFilter, setUnitFilter] = useState(UNIT_FILTER_ANY);

  const department = useQuery({
    queryKey: ['people', 'departments', departmentId],
    queryFn: () => api.get<DepartmentDetail>(`/people/departments/${departmentId}`),
    enabled: Boolean(departmentId),
    placeholderData: keepPreviousData,
  });

  const insights = useQuery({
    queryKey: ['people', 'departments', departmentId, 'insights'],
    queryFn: () => api.get<DepartmentInsights>(`/people/departments/${departmentId}/insights`),
    enabled: Boolean(departmentId),
    placeholderData: keepPreviousData,
  });

  const unitsForFilter = useQuery({
    queryKey: ['people', 'departments', departmentId, 'units', 'filter'],
    queryFn: () =>
      api.get<{ data: DepartmentUnitOption[] }>(
        `/people/departments/${departmentId}/units?pageSize=100`,
      ),
    enabled: Boolean(departmentId),
  });

  const departmentMembers = useQuery({
    queryKey: [
      'people',
      'departments',
      departmentId,
      'members',
      memberPage,
      memberPageSize,
      memberSearch,
      unitFilter,
      sortField,
      sortDirection,
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(memberPage),
        pageSize: String(memberPageSize),
        sort: toSortParam(sortField, sortDirection),
      });
      const trimmedSearch = memberSearch.trim();
      if (trimmedSearch) params.set('q', trimmedSearch);
      if (unitFilter === UNIT_FILTER_NONE) params.set('withoutUnit', 'true');
      else if (unitFilter !== UNIT_FILTER_ANY) params.set('unitId', unitFilter);
      return api.get<{
        data: Person[];
        page: { page: number; pageSize: number; totalPages: number; totalItems: number };
      }>(`/people/departments/${departmentId}/members?${params.toString()}`);
    },
    enabled: Boolean(departmentId),
    placeholderData: keepPreviousData,
  });

  const people = useQuery({
    queryKey: ['people', 'members', 'picker', memberQuery],
    queryFn: () =>
      api.get<{ data: Person[] }>(
        `/people/members?pageSize=20${memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''}`,
      ),
    enabled: addOpen,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    const row = department.data;
    if (!row || !editOpen) return;
    setName(row.name);
    setDescription(row.description ?? '');
  }, [department.data, editOpen]);

  useEffect(() => {
    if (!addOpen) {
      setSelectedPeople([]);
      setMemberQuery('');
      setPickedId(null);
    }
  }, [addOpen]);

  const memberIds = useMemo(
    () => new Set((department.data?.members ?? []).map((row) => row.id)),
    [department.data?.members],
  );
  const selectedIds = useMemo(
    () => new Set(selectedPeople.map((person) => person.id)),
    [selectedPeople],
  );

  const pickerOptions = useMemo(
    () =>
      (people.data?.data ?? [])
        .filter((person) => !memberIds.has(person.id) && !selectedIds.has(person.id))
        .map((person) => ({
          id: person.id,
          label: personLabel(person),
          description: person.phone ?? undefined,
        })),
    [memberIds, people.data?.data, selectedIds],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['people', 'departments', departmentId] });
    void queryClient.invalidateQueries({ queryKey: ['people', 'departments'] });
    void queryClient.invalidateQueries({ queryKey: ['people', 'members'] });
    void queryClient.invalidateQueries({
      queryKey: ['people', 'departments', departmentId, 'insights'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['people', 'departments', departmentId, 'members'],
    });
  };

  const unitFilterOptions = useMemo(
    () => [
      { value: UNIT_FILTER_ANY, label: t('departments.memberFilters.unitAny') },
      { value: UNIT_FILTER_NONE, label: t('departments.memberFilters.unitNone') },
      ...(unitsForFilter.data?.data ?? []).map((unit) => ({
        value: unit.id,
        label: unit.name,
      })),
    ],
    [unitsForFilter.data?.data, t],
  );

  const memberFiltersApplied = useMemo(() => {
    let count = 0;
    if (memberSearch.trim()) count += 1;
    if (unitFilter !== UNIT_FILTER_ANY) count += 1;
    return count;
  }, [memberSearch, unitFilter]);

  const save = useMutation({
    mutationFn: () =>
      api.patch<DepartmentDetail>(`/people/departments/${departmentId}`, {
        name,
        description: description.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('departments.saved'));
      setEditOpen(false);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const addMembers = useMutation({
    mutationFn: async () => {
      for (const person of selectedPeople) {
        await api.patch(`/people/members/${person.id}`, { departmentId });
      }
      return selectedPeople.length;
    },
    onSuccess: (count) => {
      toast.success(t('departments.addMembers.saved', { count }));
      setAddOpen(false);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removeMember = useMutation({
    mutationFn: (memberId: string) =>
      api.patch(`/people/members/${memberId}`, { departmentId: null }),
    onSuccess: () => {
      toast.success(t('departments.removeMember.removed'));
      setRemoveTarget(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Person>[] = [
    {
      id: 'name',
      header: t('departments.memberColumns.name'),
      sortable: true,
      cell: (row) => (
        <MemberNameWithPositions
          memberId={row.id}
          name={personLabel(row)}
          positions={row.departmentRoles ?? []}
        />
      ),
    },
    {
      id: 'units',
      header: t('departments.memberColumns.units'),
      cell: (row) => formatMemberUnits(row.units),
      hideBelowMd: true,
    },
    {
      id: 'phone',
      header: t('departments.memberColumns.phone'),
      sortable: true,
      cell: (row) =>
        row.contactHidden ? t('members.contactHidden') : (row.phone ?? '—'),
      hideBelowMd: true,
    },
    {
      id: 'type',
      header: t('departments.memberColumns.type'),
      sortable: true,
      cell: (row) => t(`members.type.${row.memberType}` as 'members.type.member'),
    },
    {
      id: 'actions',
      header: tCommon('table.actions'),
      align: 'right',
      cell: (row) => (
        <Button
          size="sm"
          variant="outline"
          onClick={(event) => {
            event.stopPropagation();
            setRemoveTarget(row);
          }}
        >
          {t('departments.removeMember.action')}
        </Button>
      ),
    },
  ];

  const selectedColumns: DataTableColumn<Person>[] = [
    {
      id: 'name',
      header: t('departments.memberColumns.name'),
      cell: (row) => <MemberNameWithPositions memberId={row.id} name={personLabel(row)} />,
    },
    {
      id: 'phone',
      header: t('departments.memberColumns.phone'),
      cell: (row) =>
        row.contactHidden ? t('members.contactHidden') : (row.phone ?? '—'),
    },
    {
      id: 'type',
      header: t('departments.memberColumns.type'),
      cell: (row) => t(`members.type.${row.memberType}` as 'members.type.member'),
    },
    {
      id: 'actions',
      header: tCommon('table.actions'),
      align: 'right',
      cell: (row) => (
        <Button
          size="sm"
          variant="outline"
          type="button"
          onClick={() =>
            setSelectedPeople((current) => current.filter((person) => person.id !== row.id))
          }
        >
          {t('departments.addMembers.removeSelected')}
        </Button>
      ),
    },
  ];

  if (department.isError && !department.data) {
    return (
      <div>
        <PageHeader
          title={t('departments.title')}
          breadcrumbs={[
            { label: t('departments.title'), to: '/people/departments' },
            { label: tCommon('state.notFoundTitle') },
          ]}
        />
        <p className="text-body-sm text-fg-secondary">{tCommon('state.notFoundBody')}</p>
      </div>
    );
  }

  if (!department.data) {
    return (
      <div>
        <PageHeader
          title={t('departments.title')}
          breadcrumbs={[
            { label: t('departments.title'), to: '/people/departments' },
            { label: tCommon('state.loading') },
          ]}
        />
        <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
      </div>
    );
  }

  const row = department.data;
  const memberRows = departmentMembers.data?.data ?? [];
  const hasMemberFilters = memberFiltersApplied > 0;

  return (
    <div>
      <PageHeader
        title={row.name}
        description={row.description || t('departments.detailDescription')}
        breadcrumbs={[
          { label: t('departments.title'), to: '/people/departments' },
          { label: row.name },
        ]}
        headerActions={
          <div className="flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              {tCommon('action.edit')}
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              {t('departments.addMembers.action')}
            </Button>
          </div>
        }
      />

      <CollapsibleInsights
        summary={[
          {
            label: t('departments.insights.members'),
            value: insights.data ? formatNumber(insights.data.memberCount) : '…',
          },
          {
            label: t('departments.insights.active'),
            value: insights.data ? formatNumber(insights.data.activeCount) : '…',
          },
          {
            label: t('departments.insights.joinedThisMonth'),
            value: insights.data ? formatNumber(insights.data.joinedThisMonth) : '…',
          },
          {
            label: t('departments.insights.birthdaysMonth'),
            value: insights.data ? formatNumber(insights.data.birthdayCount) : '…',
          },
          {
            label: t('departments.insights.units'),
            value: insights.data ? formatNumber(insights.data.unitCount) : '…',
          },
          {
            label: t('departments.insights.inUnits'),
            value: insights.data ? formatNumber(insights.data.inUnitsCount) : '…',
          },
        ]}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          <StatCard
            label={t('departments.insights.members')}
            value={insights.data ? formatNumber(insights.data.memberCount) : undefined}
            icon={<Users className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('departments.insights.active')}
            value={insights.data ? formatNumber(insights.data.activeCount) : undefined}
            icon={<UserCheck className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('departments.insights.joinedThisMonth')}
            value={insights.data ? formatNumber(insights.data.joinedThisMonth) : undefined}
            icon={<UserPlus className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('departments.insights.birthdaysMonth')}
            value={insights.data ? formatNumber(insights.data.birthdayCount) : undefined}
            icon={<Cake className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('departments.insights.units')}
            value={insights.data ? formatNumber(insights.data.unitCount) : undefined}
            icon={<Layers className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('departments.insights.inUnits')}
            value={insights.data ? formatNumber(insights.data.inUnitsCount) : undefined}
            icon={<LayoutGrid className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

        <ChartCard
          title={t('departments.insights.largestUnits')}
          loading={insights.isLoading}
          hasData={(insights.data?.largestUnits.length ?? 0) > 0}
          empty={{ title: t('departments.insights.largestUnitsEmpty') }}
          height={160}
        >
          <SimpleBarChart
            orientation="horizontal"
            data={(insights.data?.largestUnits ?? []).map((row) => ({
              label: row.label,
              value: row.value,
            }))}
            formatValue={(value) => formatNumber(value)}
            height={140}
          />
        </ChartCard>

        <div className="grid gap-4 lg:grid-cols-3">
          <ChartCard
            title={t('departments.insights.byGender')}
            loading={insights.isLoading}
            hasData={(insights.data?.byGender.length ?? 0) > 0}
            empty={{ title: t('departments.insights.breakdownEmpty') }}
            height={180}
          >
            <SimpleDonutChart
              data={(insights.data?.byGender ?? []).map((item) => ({
                label: t(`members.gender.${item.key}`, { defaultValue: item.key }),
                value: item.value,
              }))}
              formatValue={formatNumber}
              centerLabel={t('departments.insights.members')}
              centerValue={
                insights.data ? formatNumber(insights.data.memberCount) : undefined
              }
              size={148}
            />
          </ChartCard>
          <ChartCard
            title={t('departments.insights.byType')}
            loading={insights.isLoading}
            hasData={(insights.data?.byType.length ?? 0) > 0}
            empty={{ title: t('departments.insights.breakdownEmpty') }}
            height={180}
          >
            <SimpleDonutChart
              data={(insights.data?.byType ?? []).map((item) => ({
                label: t(`members.type.${item.key}`, { defaultValue: item.key }),
                value: item.value,
              }))}
              formatValue={formatNumber}
              size={148}
            />
          </ChartCard>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-body font-medium">
                <Cake className="size-4 text-fg-tertiary" aria-hidden />
                {t('departments.insights.birthdays')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {insights.isLoading ? (
                <p className="text-body-sm text-fg-muted">…</p>
              ) : (insights.data?.birthdaysThisMonth.length ?? 0) === 0 ? (
                <p className="text-body-sm text-fg-muted">
                  {t('departments.insights.birthdaysEmpty')}
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {(insights.data?.birthdaysThisMonth ?? []).map((person) => (
                    <li key={person.id}>
                      <div className="flex items-center justify-between gap-3 text-body-sm">
                        <MemberNameWithPositions
                          memberId={person.id}
                          name={personLabel(person)}
                          className="font-medium"
                        />
                        <span className="shrink-0 text-caption text-fg-tertiary tabular-nums">
                          {formatBirthdayDay(person.dateOfBirth, locale)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </CollapsibleInsights>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">{t('departments.tabs.members')}</TabsTrigger>
          <TabsTrigger value="units">{t('departments.tabs.units')}</TabsTrigger>
          <TabsTrigger value="leadership">{t('departments.tabs.leadership')}</TabsTrigger>
        </TabsList>
        <TabsContent value="members">
          <FilterBar
            appliedCount={memberFiltersApplied}
            onClearAll={() => {
              setMemberSearch('');
              setUnitFilter(UNIT_FILTER_ANY);
              setMemberPage(1);
            }}
            labels={{
              filters: t('departments.memberFilters.title'),
              clearAll: t('departments.memberFilters.clearAll'),
              applied: (count) => t('departments.memberFilters.applied', { count }),
            }}
            leading={
              <SearchInput
                value={memberSearch}
                onChange={(value) => {
                  setMemberSearch(value);
                  setMemberPage(1);
                }}
                placeholder={t('departments.memberFilters.search')}
                clearLabel={tCommon('search.clear')}
                className="max-w-none sm:w-56"
                aria-label={t('departments.memberFilters.search')}
              />
            }
          >
            {({ popoverPortal }) => (
              <div className="w-48 min-w-[11rem]">
                <Listbox
                  value={unitFilter}
                  onChange={(value) => {
                    setUnitFilter(value ?? UNIT_FILTER_ANY);
                    setMemberPage(1);
                  }}
                  options={unitFilterOptions}
                  popoverPortal={popoverPortal}
                  aria-label={t('departments.memberFilters.unit')}
                />
              </div>
            )}
          </FilterBar>
          <DataTable
            rows={memberRows}
            columns={columns}
            rowKey={(member) => member.id}
            loading={departmentMembers.isLoading}
            empty={
              hasMemberFilters ? (
                <p className="text-body-sm text-fg-secondary">
                  {t('departments.membersEmptyFiltered')}
                </p>
              ) : (
                <div className="flex flex-col items-start gap-3">
                  <p className="text-body-sm text-fg-secondary">{t('departments.membersEmpty')}</p>
                  <Button size="sm" onClick={() => setAddOpen(true)}>
                    {t('departments.addMembers.action')}
                  </Button>
                </div>
              )
            }
            sort={{
              field: sortField,
              direction: sortDirection,
              onChange: (field, direction) => {
                setSortField(field);
                setSortDirection(direction);
                setMemberPage(1);
              },
            }}
          />
          {departmentMembers.data?.page && departmentMembers.data.page.totalPages > 1 ? (
            <div className="mt-4">
              <Pagination
                page={departmentMembers.data.page.page}
                pageSize={departmentMembers.data.page.pageSize}
                totalItems={departmentMembers.data.page.totalItems}
                totalPages={departmentMembers.data.page.totalPages}
                onPageChange={setMemberPage}
                onPageSizeChange={(next) => {
                  setMemberPageSize(next);
                  setMemberPage(1);
                }}
                labels={{
                  showing: ({ from, to, total }) =>
                    tCommon('pagination.showing', { from, to, total }),
                  perPage: tCommon('pagination.perPage'),
                  first: tCommon('pagination.first'),
                  previous: tCommon('pagination.previous'),
                  next: tCommon('pagination.next'),
                  last: tCommon('pagination.last'),
                }}
              />
            </div>
          ) : null}
        </TabsContent>
        <TabsContent value="units">
          <DepartmentUnitsPanel
            departmentId={departmentId}
            canManage={canManageDepartments}
            canEditMembers={canEditMembers}
          />
        </TabsContent>
        <TabsContent value="leadership">
          <DepartmentLeadershipPanel
            departmentId={departmentId}
            canManage={canManageDepartments}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('departments.edit')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label>{t('departments.name')}</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('departments.descriptionField')}</Label>
              <Textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
                {t('departments.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent size="xl" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('departments.addMembers.title')}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              addMembers.mutate();
            }}
          >
            <DialogBody className="overflow-visible" contentClassName="space-y-4">
              <p className="text-body-sm text-fg-secondary">
                {t('departments.addMembers.description')}
              </p>
              <div className="flex flex-col gap-1.5">
                <Label>{t('departments.addMembers.people')}</Label>
                <EntityCombobox
                  value={pickedId}
                  onChange={(id) => {
                    if (!id) {
                      setPickedId(null);
                      return;
                    }
                    const person = people.data?.data.find((row) => row.id === id);
                    if (!person || memberIds.has(id) || selectedIds.has(id)) {
                      setPickedId(null);
                      return;
                    }
                    setSelectedPeople((current) => [...current, person]);
                    setPickedId(null);
                  }}
                  options={pickerOptions}
                  onQueryChange={setMemberQuery}
                  loading={people.isFetching}
                  placeholder={t('departments.addMembers.placeholder')}
                  emptyMessage={t('departments.addMembers.empty')}
                  clearLabel={tCommon('action.clearSelection')}
                  popoverPortal="dialog"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('departments.addMembers.selected')}</Label>
                <DataTable
                  rows={selectedPeople}
                  columns={selectedColumns}
                  rowKey={(person) => person.id}
                  empty={
                    <p className="text-body-sm text-fg-secondary">
                      {t('departments.addMembers.selectedEmpty')}
                    </p>
                  }
                />
              </div>
            </DialogBody>
            <div className="flex justify-end gap-2 pt-3">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                loading={addMembers.isPending}
                disabled={selectedPeople.length === 0}
              >
                {t('departments.addMembers.confirm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setRemoveTarget(null);
        }}
        title={t('departments.removeMember.title', {
          name: removeTarget ? personLabel(removeTarget) : '',
        })}
        description={t('departments.removeMember.description')}
        confirmLabel={t('departments.removeMember.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={removeMember.isPending}
        onConfirm={() => {
          if (removeTarget) removeMember.mutate(removeTarget.id);
        }}
      />
    </div>
  );
};
